import { createContext } from 'react';

type EnabledFeature = {
    id: string;
}

type FeatureToggleParameter = {
    features?: EnabledFeature[];
}

/**
 * 機能フラグ（Feature Flag）を管理するクラス
 * 
 * ドキュメント管理サービス内で特定の機能の有効/無効を制御するために使用します。
 * 各機能は一意のIDで識別され、FeatureToggleクラスのメソッドを通じて機能の状態を判定できます。
 * 機能IDはDynamoDBのlic-functionテーブル内のfunction_idと対応しています。
 */
export class FeatureToggle {
    private readonly features: EnabledFeature[];

    constructor(param?: FeatureToggleParameter) {
        param ??= {}

        param.features ??= [];

        this.features = param.features;
    }

    /**
     * 指定された機能IDが有効化されているかチェックします
     * 
     * @param id - チェックしたい機能のID
     * @returns 機能が有効化されている場合は true、それ以外は false
     * @private
     */
    private enabled(id: string): boolean {
        if (!this.features) {
            return false;
        }

        return this.features.some(f => f.id === id) === true;
    }

    allowed(): boolean { return this.enabled('webui::site'); }
    insightReporter(): boolean { return this.enabled('webui::insight-report'); }
    deleteDocument(): boolean { return this.enabled('api::deleteDocument'); }
    permanentlyDeleteDocument(): boolean { return this.enabled('api::permanentlyDeleteDocument'); }
    sendDocument(): boolean { return this.enabled('api::createDocumentUrl'); }
    isTenantFeatureSetting(): boolean { return this.enabled('api::enabledTenantFeatureSetting'); }
    registerKintoneSetting(): boolean { return this.enabled('api::registerKintoneSetting'); }
    multiDeviceLink(): boolean { return this.enabled('multiDeviceLink'); }
    canUpdateDeviceDisplayName(): boolean { return this.enabled('api::updateDeviceDisplayName'); }
}

/**
 * 機能フラグを React コンポーネント間で共有するための Context
 * 
 * ドキュメント管理サービス内で機能フラグの状態を一元管理し、
 * 各コンポーネントで機能の有効/無効を判定するために使用します。
 * 
 * @example
 * ```tsx
 * // コンポーネントでの使用例
 * import { useContext } from 'react';
 * import { FeatureContext } from './FeatureContext';
 *   
 * const features = React.useContext(FeatureContext);
 * // features.allowed() などで機能の有効/無効を判定
 * if(features.canUpdateDeviceDisplayName){
 * //機能の権限がある場合の処理
 * }
 * ```
 */
export const FeatureContext = createContext<FeatureToggle>(new FeatureToggle());
